import React, { useState, useEffect, useRef } from 'react';
import { Tag, Input, Separator, useTheme, useUpdateModal } from '@zydalabs/storefront-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Button, Typography, useTheme as useZacTheme } from '@zydalabs/zac-react';
import dynamic from 'next/dynamic';

import { useCreateFacebookCapi, useLayout, useProductDetails, useStockThresholdSettings } from 'service/hooks';
import { COUNTER_TYPE } from 'common/constants/counterTypes';
import {
  DATA_LAYER_CONTENT_TYPE,
  FACEBOOK_PIXEL_EVENTS,
  FULFILLMENT_MODES,
  LANGUAGE_AR,
  ORDER_TIME_TYPES,
  SNAPCHAT_EVENTS,
  TIKTOK_EVENTS,
  URLS,
} from 'common/constants';
import { CustomActionsRegistry } from 'common/lib/registries';
import { CustomActionsEnum } from 'common/enums';
import {
  discountTagText,
  displayCurrentSelectionPrice,
  getPrepTimeText,
  sanitizeTitleString,
  handleScheduledTime,
  optionsSelectedPrice,
  handleBusyBranchModal,
} from 'modules/productsModule/utils';
import { CartItemOptions, Variant } from 'service/types/generated';
import {
  usePersistedCartData,
  useGlobalSnackBar,
  usePersistedCountryDetails,
  useGlobalOrderTypeAndTime,
  useGlobalProductDetails,
  useResponsive,
  useGlobalDefaultOrderMode,
  usePersistedSupportedDeliveryZone,
  useUserData,
  usePersistedSelectedOptions,
} from 'common/hooks';
import { useCartDetails, useCartItem } from 'service/hooks/cart';
import { SectionWrapper, StyledPageWrapper, StyledStickyButton } from 'common/styles';
import {
  normalizeFulfillmentMode,
  changeRoute,
  localizeCurrency,
  customHistory,
  replaceRoute,
  isItemPublishedInCurrentBranch,
  SessionStorage,
  trackFacebookEvent,
  redirectToOrderFast,
  maskOrderFastUrl,
  trackTiktokEvents,
  localizeNumber,
} from 'common/utils';
import { useUpdateItemInCart } from 'modules/productsModule/hooks';
import Counter, { CounterVariantType } from 'modules/productsModule/components/Counter';
import MenuItemImages from 'modules/productsModule/components/MenuItemImages';
import MenuItemVariants from 'modules/productsModule/components/MenuItemVariants';
import MenuItemOptionGroup from 'modules/productsModule/components/MenuItemOptionGroup';
import ProductDetailsShimmer from 'modules/productsModule/components/ProductDetailsShimmer';
import { pushAddToCartEventToDataLayer } from 'common/lib/tracking/utils';
import ProductDetailsHeader from 'modules/productsModule/components/ProductDetailsHeader';
import ServiceConfigs from 'service/config';
import { trackSnapchatEvents } from 'common/utils/trackSnapchatEvents';
import { isBranchBusyAtDateTime } from 'modules/checkoutModule/utils';
import { DateTime } from 'luxon';
import { useAddToCart } from '../../hooks/cart';
import {
  StyledInfo,
  StyledImageWrapper,
  StyledOldPrice,
  StyledPriceWrapper,
  StyledDescriptionWrapper,
  StyledTitleWrapper,
  StyledBulkWrapper,
} from './style';
import { translateDisplayPrice, itemAvailabilityInfo, checkIfAllVariantsAreAvailable } from '../../utils';
import { useFulfillmentSettings } from '../../../../contexts';

const OrderModeActionSheetComponent = dynamic(() => import('modules/productsModule/components/orderMode/OrderModeActionSheet'));

const ProductDetails: React.FC = () => {
  const router = useRouter();
  const { colors }: any = useZacTheme();
  const theme: any = useTheme();
  const updateModal = useUpdateModal();
  const isArabic = router.locale === LANGUAGE_AR;
  const { t, lang } = useTranslation('product');
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const { currency, code } = persistedCountryDetails || {};
  const { userDataBackEnd, capiEnabled, tiktokData } = useUserData();
  const createCapiEvent = useCreateFacebookCapi();
  const isUserLoggedIn = !!ServiceConfigs.getUserToken();
  const useDeliveryzonesMs = ServiceConfigs?.getUseDeliveryzonesMs();
  const [persistedCartOptions, setPersistedCartOptions, removePersistedCartOptions] = usePersistedSelectedOptions();

  const [cartData] = usePersistedCartData();
  const [supportedDeliveryZone] = usePersistedSupportedDeliveryZone();
  const menuItemId = router.query.id as string;
  const cartItemId = router.query.cartItemId as string;
  const [globalOrderTypeAndTime] = useGlobalOrderTypeAndTime();
  const [globalProductDetails, setGlobalProductDetails] = useGlobalProductDetails();
  const { area, orderMode, branch } = useFulfillmentSettings();
  const { busyMode, busyFrom, busyUntil } = branch || {};
  const [globalDefaultOrderMode] = useGlobalDefaultOrderMode();
  const { fulfillmentTimeType, scheduleSlotInterval } = globalOrderTypeAndTime || {};
  const scheduledTime = handleScheduledTime(fulfillmentTimeType, scheduleSlotInterval);

  const [isOrderModeActionSheetOpen, setIsOrderModeActionSheetOpen] = useState<boolean>(false);

  const [shouldInvalidateProduct, setShouldInvalidateProduct] = useState<boolean>(true);

  const {
    data: productDetails,
    isLoading: isProductLoading,
    mutate: mutateProductDetails,
  } = useProductDetails({
    uuid: menuItemId,
    branchId: branch?.id,
    shouldInvalidate: shouldInvalidateProduct,
    scheduledTime,
  });
  const {
    data: cartDetails,
    isLoading: isCartDetailsLoading,
    mutate: mutateCartDetails,
  } = useCartDetails({
    ...cartData,
    config: { revalidateOnMount: true },
  });

  const { data: stockThresholdSettings, isLoading: isStockThresholdSettingsLoading } = useStockThresholdSettings();

  const {
    data: cartItemData,
    isLoading: cartItemLoading,
    mutate: mutateCartItems,
  } = useCartItem({
    ...cartData,
    cartItemId,
    config: { revalidateOnMount: true },
  });

  const { data: storeData, isLoading: isStoreLoading } = useLayout();
  const { defaultProductPrepTime, setting } = storeData || {};

  const addItemToCart = useAddToCart({
    branchId: branch?.id,
    deliveryType: normalizeFulfillmentMode(orderMode),
    areaId: area?.id,
    ...(useDeliveryzonesMs &&
      !!supportedDeliveryZone &&
      orderMode === FULFILLMENT_MODES.DELIVERY && {
        deliveryZone: {
          ...supportedDeliveryZone,
        },
      }),
  });
  const updateCartItem = useUpdateItemInCart();

  const [selectedVariant, setSelectedVariant] = useState<Variant>();

  const [selectedOptions, setSelectedOptions] = useState<Array<CartItemOptions>>(persistedCartOptions?.selectedOptions || []);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [isAddingItem, setIsAddingItem] = useState<boolean>(false);
  const [optionGroupsErrors, setOptionGroupsErrors] = useState<
    Record<string, { hasError: boolean; optionGroupRef: React.RefObject<HTMLDivElement> }>
  >({});
  const [oldVariantCartQuantity, setOldVariantCartQuantity] = useState<number>(0);
  const currentSelectionInitPrice = selectedVariant ? selectedVariant?.discountedPrice || selectedVariant?.price : 0;
  const [priceForCurrentSelection, setPriceForCurrentSelection] = useState<number>(currentSelectionInitPrice);

  const notesRef = useRef<HTMLDivElement>();
  const isAddedToCart = useRef<boolean>(false);

  const [notes, setNotes] = useState<string>(persistedCartOptions?.specialNote || globalProductDetails?.specialNote || '');
  const [currentQuantity, setCurrentQuantity] = useState<number>(
    persistedCartOptions?.currentQuantity || globalProductDetails?.currentQuantity || 1,
  );
  const isLoading =
    isProductLoading ||
    isCartDetailsLoading ||
    isStoreLoading ||
    isStockThresholdSettingsLoading ||
    (cartItemId && cartItemLoading);
  const relatedCartItemsRef = useRef([]);
  const [snackBarDetails, setSnackBarDetails] = useGlobalSnackBar();
  const isLargeScreen = useResponsive(`min-width: ${theme.screenBreakpoints.extraLarge}`);

  const {
    id: variantId,
    images: variantImages,
    stockData: variantStockData,
    maximumQuantity: maximumBulkQuantity,
  } = selectedVariant || {};

  const {
    titleEn: productTitleEn,
    titleAr: productTitleAr,
    descriptionEn: productDescriptionEn,
    descriptionAr: productDescriptionAr,
    photoUrl: productPhotoUrl,
    variantsTitleEn: productVariantsTitleEn,
    variantsTitleAr: productVariantsTitleAr,
    variants: productVariants,
    highestDiscountedPercentage: productHighestDiscountedPercentage,
    displayPrice: productDisplayPrice,
    fulfillmentMode: productFulfillmentMode,
    maxPrepTime,
    specialNotesEnabled,
    specialNotesPlaceholderAr,
    specialNotesPlaceholderEn,
    specialNotesRequired,
    quickAddToCart,
    stockData: productStockData,
    publishedBranchIds,
    categoryIds,
  } = productDetails || {};

  const {
    availabilityText: productAvailabilityText,
    availabilityTagVariant: productAvailabilityTagVariant,
    unavailabilityReason: productUnavailabilityReason,
  } = checkIfAllVariantsAreAvailable({
    productVariants,
    t,
    lang,
    shouldInvalidateProduct,
    productFulfillmentMode,
    orderMode,
    stockThresholdSettings,
    productStockData,
  });

  const {
    unavailabilityReason: variantUnavailabilityReason,
    trackingType: variantTrackingType,
    stockCount: variantStockCount,
  } = itemAvailabilityInfo({
    t,
    stockData: productVariants?.length === 1 ? productVariants?.[0]?.stockData : variantStockData,
    shouldInvalidateProduct,
    lang,
    productFulfillmentMode,
    mode: orderMode,
    stockThresholdSettings,
  });

  const [isBulk, setIsBulk] = useState<boolean>(false);
  const [bulkCount, setBulkCount] = useState<number>(0);
  const [isBulkNotValid, setIsBulkNotValid] = useState<boolean>(false);

  useEffect(() => {
    if (selectedVariant?.maximumQuantity > 0) {
      setIsBulk(true);
    } else {
      setIsBulk(false);
    }
  }, [selectedVariant?.optionGroups, selectedVariant]);

  useEffect(() => {
    if (persistedCartOptions) {
      const { selectedVariant: prevSelectedVariants, selectedOptions: prevSelectedOptions } = persistedCartOptions || {};
      const availableSelectedVariant = productDetails?.variants.find(variant => variant.id === prevSelectedVariants?.id);
      setSelectedOptions(prevSelectedOptions);
      setBulkCount(prevSelectedOptions?.reduce((acc, val) => acc + val.optionQuantity, 0));

      if (availableSelectedVariant) {
        setSelectedVariant(availableSelectedVariant);
      }
    }
  }, [productDetails]);

  const isBranchSelected = !!branch?.id;

  const exceedsAvailableStock =
    !!variantTrackingType &&
    (cartItemId ? currentQuantity > variantStockCount : currentQuantity + oldVariantCartQuantity > variantStockCount);

  const shouldDisableAddToCart = !selectedVariant || !currentQuantity || !!variantUnavailabilityReason || exceedsAvailableStock;
  const isPersistedModeBusy = isBranchBusyAtDateTime(DateTime.now(), orderMode, busyMode, busyFrom, busyUntil);

  useEffect(() => {
    if (fulfillmentTimeType === undefined) return;
    const shouldShowModal = isPersistedModeBusy && fulfillmentTimeType !== ORDER_TIME_TYPES.SCHEDULE;

    if (shouldShowModal) {
      handleBusyBranchModal({
        branch,
        orderMode,
        scheduleOrdersEnabled: setting?.scheduleOrdersEnabled,
        t,
        updateModal,
      });
    }
  }, [branch, orderMode, fulfillmentTimeType]);

  const onActionButtonClick = async () => {
    if (!isUserLoggedIn) {
      setPersistedCartOptions({
        selectedVariant,
        selectedOptions,
        currentQuantity,
        specialNote: notes,
      });
      redirectToOrderFast({
        path: maskOrderFastUrl(`${URLS.PRODUCTS}/${menuItemId}`),
        locale: router.locale,
        countryCode: code,
      });
      return;
    }
    CustomActionsRegistry.setCustomAction({
      key: CustomActionsEnum.MutateCartDetails,
      fn: () => {
        setShouldInvalidateProduct(true);
        mutateCartDetails();
      },
    });
    if (shouldDisableAddToCart) return;

    if (!useDeliveryzonesMs && !isBranchSelected) {
      setGlobalProductDetails({
        selectedVariant,
        selectedOptions,
        currentQuantity,
        specialNote: notes,
      });
      changeRoute({
        pathname: URLS.ORDER_MODE,
        query: {
          mode: globalDefaultOrderMode,
        },
      });
      return;
    }
    const bulkValidation = isBulk && bulkCount < maximumBulkQuantity;
    if (bulkValidation) {
      setIsBulkNotValid(true);
      return;
    }

    CustomActionsRegistry.setCustomAction({
      key: CustomActionsEnum.RemoveUnavailableOptions,
      fn: async () => {
        const updatedProductDetails = await mutateProductDetails();

        const validOptionIds = new Set(
          updatedProductDetails?.variants
            ?.flatMap(variant => variant.optionGroups)
            ?.flatMap(group => group?.options)
            ?.filter(option => option?.publishedInCurrentBranch)
            ?.map(option => option.uuid),
        );
        const validSelectedOptions = selectedOptions.filter(option => validOptionIds.has(option.optionId));

        setSelectedOptions(validSelectedOptions);
      },
    });

    const firstOptionGroupHasError = Object.values(optionGroupsErrors).find(
      optionGroupError => optionGroupError?.hasError === true,
    );
    let response;
    const isNewItem = !cartItemId;

    if ((isBranchSelected || (useDeliveryzonesMs && firstOptionGroupHasError)) && isUserLoggedIn) {
      setShowValidation(true);
      if (firstOptionGroupHasError || (specialNotesRequired && !notes)) {
        if (firstOptionGroupHasError) firstOptionGroupHasError.optionGroupRef?.current.scrollIntoView();
        else notesRef.current.scrollIntoView();
        showErrorInSnackBar();
      } else {
        setIsAddingItem(true);
        if (isNewItem) {
          response = await addItemToCart({
            menuItemId,
            variantQuantity: currentQuantity,
            variantId,
            options: selectedOptions,
            notes,
          });
        } else {
          response = await updateCartItem({
            cartItemId,
            menuItemId,
            variantId,
            variantQuantity: currentQuantity,
            options: selectedOptions,
            notes,
          });
        }
        setIsAddingItem(false);

        if (!response?.hasError) {
          if (isNewItem) {
            pushAddToCartEventToDataLayer({
              contents: [
                {
                  item_id: menuItemId,
                  quantity: currentQuantity || 1,
                  item_variant: selectedVariant.titleEn,
                  item_name: productTitleEn,
                  price: priceForCurrentSelection / (currentQuantity || 1),
                },
              ],
              content_type: DATA_LAYER_CONTENT_TYPE.PRODUCT,
              currency: currency.title_en,
              value: priceForCurrentSelection || 0,
            });
            trackFacebookEvent(
              FACEBOOK_PIXEL_EVENTS.ADD_TO_CART,
              {
                content_ids: menuItemId,
                content_type: DATA_LAYER_CONTENT_TYPE.PRODUCT,
                currency: currency.title_en,
                value: priceForCurrentSelection || 0,
                content_name: isArabic ? productTitleAr : productTitleEn,
              },
              userDataBackEnd,
              createCapiEvent,
              capiEnabled,
            );
            trackSnapchatEvents(SNAPCHAT_EVENTS.ADD_TO_CART, {
              currency: currency.title_en,
              price: priceForCurrentSelection || 0,
              item_ids: [menuItemId],
              item_category: isArabic ? productTitleAr : productTitleEn,
              number_items: 1,
            });
            trackTiktokEvents(
              TIKTOK_EVENTS.ADD_TO_CART,
              {
                currency: currency.title_en,
                value: priceForCurrentSelection || 0,
                content_type: 'product',
                content_id: menuItemId,
                quantity: 1,
              },
              tiktokData,
            );
          }
          setGlobalProductDetails({});
          removePersistedCartOptions();
          if (customHistory.getPrevPath() === URLS.SEARCH) changeRoute(URLS.HOME);
          else navigateToPreviousPage();
        } else {
          mutateCartItems();
        }
      }
    } else {
      setGlobalProductDetails({
        selectedVariant,
        selectedOptions,
        currentQuantity,
        specialNote: notes,
      });
      if (useDeliveryzonesMs) {
        setIsOrderModeActionSheetOpen(true);
      }
    }
  };
  useEffect(() => {
    if (cartItemId || isLoading || !!productUnavailabilityReason) return;
    if (!globalProductDetails?.selectedVariant && !persistedCartOptions?.selectedVariant) {
      const firstAvailableVariant = productVariants?.find(variant => {
        const { trackingType, stockCount, unavailable } = variant.stockData || {};
        const isAvailable = !unavailable && (!trackingType || stockCount);
        return isAvailable;
      });
      setSelectedVariant(firstAvailableVariant);
    }
  }, [cartItemId, productVariants, isLoading]);

  useEffect(() => {
    if (cartItemData && !isLoading) {
      const { variantQuantity, options, variant, notes: cartItemNotes } = cartItemData || {};
      const fullVariantData = productVariants?.find(v => v.id === variant?.id);
      setSelectedVariant(fullVariantData || variant);
      setCurrentQuantity(variantQuantity);
      if (cartItemNotes) setNotes(cartItemNotes);
      if (options) {
        const allOptionsMapped = options?.map(currentOption => ({
          optionId: currentOption.option?.uuid,
          optionGroupId: currentOption.optionGroup?.uuid,
          optionQuantity: currentOption.optionQuantity,
        }));
        if (allOptionsMapped) {
          const optionMap = new Map();
          allOptionsMapped.forEach(opt => {
            const key = `${opt.optionId}-${opt.optionGroupId}`;
            if (optionMap.has(key)) {
              const existingOpt = optionMap.get(key);
              existingOpt.optionQuantity += opt.optionQuantity;
            } else {
              optionMap.set(key, { ...opt });
            }
          });

          const combinedOptions = Array.from(optionMap.values());
          setSelectedOptions(combinedOptions);

          const totalBulkCount = allOptionsMapped.reduce((acc, val) => acc + val.optionQuantity, 0);
          setBulkCount(totalBulkCount);
        }
      }
    }
  }, [cartItemData, isLoading]);

  useEffect(() => {
    if (cartDetails)
      relatedCartItemsRef.current = cartDetails.cartItems?.filter(cartItem => cartItem.menuItem.uuid === menuItemId);
  }, [cartDetails]);

  useEffect(() => {
    const filterGlobalProductDetails = () => {
      const { selectedVariant: globalSelectedVariant, selectedOptions: globalSelectedOptions } = globalProductDetails || {};
      const availableSelectedVariant = productDetails?.variants.find(variant => variant.id === globalSelectedVariant?.id);
      setSelectedOptions(globalSelectedOptions);
      setBulkCount(globalSelectedOptions.reduce((acc, val) => acc + val.optionQuantity, 0));
      if (availableSelectedVariant) {
        setSelectedVariant(availableSelectedVariant);
        // price = availableSelectedVariant?.discountedPrice || availableSelectedVariant?.price;
      }
    };
    if (globalProductDetails && globalProductDetails.selectedVariant) {
      filterGlobalProductDetails();
    }

    if (productDetails && !orderMode) {
      SessionStorage.setProductDetails({
        publishedBranchIds,
        categoryIds,
      });
    }
  }, [productDetails]);

  useEffect(() => {
    if (selectedVariant) {
      const { id } = selectedVariant;
      const variantCartQuantity = relatedCartItemsRef.current.reduce((accumulator, current) => {
        if (current.variant.id === id) return accumulator + (current.variantQuantity || 0);
        return accumulator;
      }, 0);
      setOldVariantCartQuantity(variantCartQuantity);

      const currentSelectionPrice =
        currentQuantity *
        ((selectedVariant?.discountedPrice || selectedVariant?.price) +
          optionsSelectedPrice({ selectedOptions, selectedVariant }));

      setPriceForCurrentSelection(currentSelectionPrice);
    }
  }, [selectedVariant, optionsSelectedPrice({ selectedOptions, selectedVariant }), currentQuantity, cartDetails]);

  useEffect(() => {
    if (!orderMode && useDeliveryzonesMs) {
      replaceRoute({ pathname: URLS.PRODUCT_DETAILS, query: { id: router.query.id, addToCart: true } });
    }
  }, [orderMode, useDeliveryzonesMs]);

  useEffect(() => {
    if (!isLoading) {
      if (!userDataBackEnd) {
        return;
      }
      trackFacebookEvent(
        FACEBOOK_PIXEL_EVENTS.VIEW_CONTENT,
        {
          content_ids: menuItemId,
          content_type: DATA_LAYER_CONTENT_TYPE.PRODUCT,
        },
        userDataBackEnd,
        createCapiEvent,
        capiEnabled,
      );
    }
  }, [isLoading]);

  useEffect(() => {
    /* this happens when all needed data are fetched (ex: product details) and all option groups are rendered and
      have no errors because all required options are set before
     */
    const isPageReadyToAddToCart = () => {
      const numOfRequiredOptionGroups = selectedVariant?.optionGroups?.reduce(
        (acc, cur) => acc + (cur.minQuantity > 0 ? 1 : 0),
        0,
      );
      const numOfOptionGroupWithErrors = Object.values(optionGroupsErrors).reduce(
        (acc, cur) => acc + (cur.hasError ? 1 : 0),
        0,
      );
      const optionGroupsErrorsValues = Object.values(optionGroupsErrors);
      const optionGroupsAreRendered =
        numOfRequiredOptionGroups <= optionGroupsErrorsValues?.length && numOfOptionGroupWithErrors === 0;

      return optionGroupsAreRendered;
    };
    const isTimeCalculated = fulfillmentTimeType || SessionStorage.getProductDetails() === null;
    const handleAddingToCart = async () => {
      if (
        router.query.addToCart &&
        isBranchSelected &&
        selectedVariant &&
        isPageReadyToAddToCart() &&
        !isAddedToCart.current &&
        isTimeCalculated &&
        !isProductLoading
      ) {
        isAddedToCart.current = true;
        await replaceRoute({ pathname: URLS.PRODUCT_DETAILS, query: { id: router.query.id } }, { shallow: true });
        if (!variantUnavailabilityReason) {
          if (isItemPublishedInCurrentBranch(branch?.id, publishedBranchIds)) {
            if (!isPersistedModeBusy || fulfillmentTimeType === ORDER_TIME_TYPES.SCHEDULE) {
              SessionStorage.removeProductDetails();
              onActionButtonClick();
            }
          } else {
            customHistory.handleOrderModeRouting({ branchId: branch?.id });
          }
        }
      }
    };
    handleAddingToCart();
  }, [variantUnavailabilityReason, branch, orderMode, selectedVariant, isPersistedModeBusy, fulfillmentTimeType]);

  if (isLoading) return <ProductDetailsShimmer />;

  const isNonEditingVariantInCart = variantID =>
    cartItemId && relatedCartItemsRef.current.some(item => item.variant.id === variantID && item.id !== cartItemId);

  const onVariantSelection = value => {
    const chosenVariant = productVariants.find(variant => variant.id === value);
    setSelectedVariant(chosenVariant);
    const existingCartItem = relatedCartItemsRef.current.find(item => item.variant.id === value);

    if (cartItemId && existingCartItem) {
      const variantBulkCount = existingCartItem.options?.reduce((acc, opt) => acc + (opt.optionQuantity || 0), 0) || 0;
      const cartOptions =
        existingCartItem.options?.map(opt => ({
          optionId: opt.option?.uuid,
          optionGroupId: opt.optionGroup?.uuid,
          optionQuantity: opt.optionQuantity,
        })) || [];

      setOldVariantCartQuantity(existingCartItem.variantQuantity || 0);
      setCurrentQuantity(existingCartItem.variantQuantity || 1);
      setBulkCount(variantBulkCount);
      setSelectedOptions(cartOptions);
    } else {
      setOldVariantCartQuantity(0);
      setCurrentQuantity(1);
      setBulkCount(0);
      setSelectedOptions([]);
    }
    setShowValidation(false);
    setOptionGroupsErrors({});
  };

  const navigateToPreviousPage = () => {
    const prevPathValue = customHistory.getPrevPath();
    if (!prevPathValue || ['null', URLS.ESTIMATED_TIME, URLS.ORDER_MODE, URLS.HOME].includes(prevPathValue)) {
      changeRoute(URLS.HOME);
    } else {
      customHistory.back();
    }
  };

  const showErrorInSnackBar = () => {
    setSnackBarDetails({
      show: true,
      setShowSnackBar: () => snackBarDetails.setShowSnackBar(true),
      autoHide: true,
      position: 'top',
      message: t('checkErrors'),
      onHide: () => setSnackBarDetails({ show: false, setShowSnackBar: () => snackBarDetails.setShowSnackBar(false) }),
    });
  };
  const productDetailsActionButtonText = isBranchSelected || useDeliveryzonesMs ? t('addToCart') : t('startOrdering');
  const isLoggedInBtnText = isUserLoggedIn ? productDetailsActionButtonText : t('startOrdering');
  const actionButtonText = exceedsAvailableStock
    ? t('exceedsAvailableStock')
    : cartItemId
    ? t('updateItem')
    : isLoggedInBtnText;

  let specialNotesPlaceholder = router.locale === LANGUAGE_AR ? specialNotesPlaceholderAr : specialNotesPlaceholderEn;
  specialNotesPlaceholder = `${specialNotesPlaceholder || t('addInstructions')}`;

  const currencyTitle = isArabic ? currency?.title_ar : currency?.title_en;
  const showMaxPrepTimeAvailable = maxPrepTime > defaultProductPrepTime && productVariants?.length === 1;

  const icMargin = isArabic ? `0 0 ${theme.space[2]}px ${theme.space[2]}px` : `0 ${theme.space[2]}px ${theme.space[2]}px 0`;
  const productTitle = isArabic ? productTitleAr : productTitleEn;
  const modifiedProductTitle = sanitizeTitleString(productTitle);
  const bulkErrorColor = isBulkNotValid ? colors.red.primary : '';
  const bulkValidationColorization = bulkCount === maximumBulkQuantity ? colors.green.primary : bulkErrorColor;
  return (
    <StyledPageWrapper style={{ background: colors?.positiveAccent?.background }}>
      <ProductDetailsHeader
        onBack={() => {
          setGlobalProductDetails({});
          removePersistedCartOptions();
          navigateToPreviousPage();
        }}
        productTitle={modifiedProductTitle}
      />
      <StyledImageWrapper>
        <MenuItemImages itemImage={productPhotoUrl} variantImages={variantImages} />
      </StyledImageWrapper>

      <StyledInfo>
        <StyledTitleWrapper>
          <Typography
            color={colors?.positiveAccent?.primary}
            variant={isLargeScreen ? 'headingExpressive32' : 'headingExpressive20'}
            mb={8}
            mt={0}
            testId="product-title"
          >
            {modifiedProductTitle}
          </Typography>
        </StyledTitleWrapper>
        <StyledDescriptionWrapper>
          <Typography variant="body14" color={colors?.positiveAccent?.secondary} mt={0} mb={0} testId="product-desc">
            {isArabic ? productDescriptionAr : productDescriptionEn}
          </Typography>
        </StyledDescriptionWrapper>

        {productAvailabilityText && (
          <Tag variant={productAvailabilityTagVariant} shape="circle" margin={icMargin} data-testid="product-availability">
            {productAvailabilityText}
          </Tag>
        )}
        {productHighestDiscountedPercentage && (
          <>
            <Tag variant="success" shape="circle" margin={icMargin} data-testid="product-percentage-tag">
              {discountTagText(t, quickAddToCart, lang, productHighestDiscountedPercentage)}
            </Tag>
          </>
        )}
        {showMaxPrepTimeAvailable && (
          <Tag variant="warning" shape="circle" mb={2} data-testid="custom-prepTime">
            {`${getPrepTimeText(maxPrepTime, lang)}`}
          </Tag>
        )}

        {productVariants?.length === 1 && currencyTitle && !!productDisplayPrice && (
          <StyledPriceWrapper>
            <Typography variant="body14" m={0} testId="product-price">
              {translateDisplayPrice(productDisplayPrice, t, lang, currencyTitle, isArabic)}
            </Typography>
            {!!productVariants?.[0]?.discountedPrice && currency && (
              <StyledOldPrice>
                <Typography variant="body14" color={theme.color.disabled} mx={8} testId="product-old-price">
                  {localizeCurrency(lang, {
                    amount: productVariants?.[0].price || Number(productDisplayPrice),
                    currency: currency.title_en,
                  })}
                </Typography>
              </StyledOldPrice>
            )}
          </StyledPriceWrapper>
        )}
      </StyledInfo>

      {productVariants?.length > 1 && (
        <SectionWrapper data-testid="variants">
          <Separator my={0} />
          <MenuItemVariants
            variants={productVariants}
            title={isArabic ? productVariantsTitleAr : productVariantsTitleEn}
            productPrepTime={defaultProductPrepTime}
            onVariantSelection={onVariantSelection}
            selectedVariantId={variantId}
            isArabic={isArabic}
            stockThresholdSettings={stockThresholdSettings}
            productFulfillmentMode={productFulfillmentMode}
            mode={orderMode}
            isNonEditingVariantInCart={isNonEditingVariantInCart}
          />
        </SectionWrapper>
      )}

      {selectedVariant?.optionGroups?.map(currentOptionGroup => {
        const stepBy = currentOptionGroup.stepBy || 1;
        const isBulkLimitReached =
          !!maximumBulkQuantity && (bulkCount >= maximumBulkQuantity || stepBy + bulkCount > maximumBulkQuantity);
        return (
          currentOptionGroup.options?.length > 0 && (
            <SectionWrapper key={`${selectedVariant.id} - ${currentOptionGroup.uuid}`} m={0}>
              <Separator my={0} />
              <MenuItemOptionGroup
                optionGroup={currentOptionGroup}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                setBulkCount={setBulkCount}
                maximumBulkQuantity={maximumBulkQuantity}
                toolTipText={isBulk && t('additionBatch', { stepBy: localizeNumber(lang, stepBy) })}
                stepBy={stepBy}
                isBulkLimitReached={isBulkLimitReached}
                showValidation={showValidation}
                setHasError={optionGroupError => {
                  setOptionGroupsErrors(oldHasError => ({ ...oldHasError, ...optionGroupError }));
                }}
                isArabic={isArabic}
              />
            </SectionWrapper>
          )
        );
      })}

      {!!(selectedVariant && specialNotesEnabled) && (
        <SectionWrapper ref={notesRef}>
          <Separator my={0} />
          <Typography variant="heading16" mt={16} mb={8}>
            {t('specialInstructions')}
          </Typography>
          <Input
            value={notes}
            onChange={({ target }) => setNotes(target.value)}
            placeholder={specialNotesPlaceholder}
            errorMessage={showValidation && specialNotesRequired && !notes && t('required')}
            grayed
          />
        </SectionWrapper>
      )}
      <StyledStickyButton>
        <StyledBulkWrapper shouldShowBulk={isBulk && bulkCount >= 0}>
          {isBulk && bulkCount >= 0 && (
            <Typography color={bulkValidationColorization}>
              {t('optionsOf', {
                maximumBulkQuantity: localizeNumber(lang, maximumBulkQuantity),
                bulkCount: localizeNumber(lang, bulkCount),
              })}
            </Typography>
          )}
          {isUserLoggedIn && (
            <Counter
              count={currentQuantity}
              onChange={setCurrentQuantity}
              maxCount={!!variantTrackingType && (cartItemId ? variantStockCount : variantStockCount - oldVariantCartQuantity)}
              minCount={1}
              variant={COUNTER_TYPE.MULTIADDON as CounterVariantType}
              disabled={!selectedVariant || !!variantUnavailabilityReason}
            />
          )}
        </StyledBulkWrapper>
        <Button
          isFluid
          onClick={onActionButtonClick}
          text={actionButtonText}
          testId="product-action-btn"
          isLoading={isAddingItem}
          isDisabled={shouldDisableAddToCart}
          endIcon={
            !!selectedVariant &&
            isBranchSelected && (
              <Typography variant="body14" testId="cta-button-product-price" color={colors.negative.primary}>
                {displayCurrentSelectionPrice({
                  t,
                  selectedVariant,
                  selectedOptions,
                  currency,
                  priceForCurrentSelection,
                  lang,
                })}
              </Typography>
            )
          }
        />
      </StyledStickyButton>
      <OrderModeActionSheetComponent
        isOrderModeActionSheetOpen={isOrderModeActionSheetOpen}
        setIsOrderModeActionSheetOpen={setIsOrderModeActionSheetOpen}
        onModeClicked={mode => {
          setIsOrderModeActionSheetOpen(false);
          changeRoute({
            pathname: URLS.ORDER_MODE,
            query: {
              mode,
            },
          });
        }}
      />
    </StyledPageWrapper>
  );
};

export default ProductDetails;
